import { resolveScaleAnchors } from '@digital-twins/deck-ui/components/charts/MatrixChart/scaleAnchors'
import { MetricResult, RatingStatistics, isRatingUnitId, ratingBounds } from '@digital-twins/shared-types'

import { findSurveyQuestion } from '@/features/studies/report/utils/findSurveyQuestion'

/**
 * The slice of analysis this resolver reads. `CanonicalAnalysisOutput` is
 * assignable. `survey` is a sidecar the report already has — analysis itself
 * does not carry the compiled SurveyJS JSON.
 */
export interface MetricFigureSource {
  metricResults?: Record<string, MetricResult> | null
  /**
   * Compiled SurveyJS JSON. Used only to read scale-anchor wording from the
   * metric's question or matrix column. Omit it (or leave the column
   * unresolved) and `scaleAnchors` is omitted — never invent a cut.
   */
  survey?: unknown
}

/**
 * Endpoint wording for the metric's rating scale. Copied from deck-ui
 * `resolveScaleAnchors` when the survey column actually has wording. The
 * favorable cut is NOT here — that lives on `favorableLabel` / `favorablePercent`.
 */
export interface MetricFigureScaleAnchors {
  minLabel: string
  midLabel: string
  maxLabel: string
}

/**
 * One stated figure read off a single `MetricResult`.
 *
 * The number and its label come from the same `RatingStatistics`, so they
 * cannot disagree. Per-asset values come from `breakdown` (native means) and
 * optional `cellRatingStatistics` — never from the 0–1 `assetScores` index.
 */
export interface MetricFigure {
  metricId: string
  label: string
  nativeValue: number | null
  /** Machine unit (`rating_1_5`) when `RatingStatistics.unit` is present, else `display.nativeUnitLabel`. */
  unit: string
  higherIsBetter?: boolean
  baseSize: number
  favorablePercent?: number | null
  /** `ratingStatistics.labels.favorable` — the cut that `favorablePercent` counts. */
  favorableLabel?: string
  scaleAnchors?: MetricFigureScaleAnchors
  assetId?: string
  /**
   * The question this metric was cut from, in the words it was asked in. The
   * page reads it from HERE rather than from `metricResults` directly, so a
   * figure and the question printed beside it can never come from two
   * different reads.
   */
  questionText?: string | null
}

function asLaneMetric(metric: MetricResult): {
  scope?: string | null
  perAsset?: Record<string, MetricResult> | null
} {
  return metric as MetricResult & {
    scope?: string | null
    perAsset?: Record<string, MetricResult> | null
  }
}

export function metricIsLaneMerged(metric: MetricResult | null | undefined): boolean {
  if (!metric) {
    return false
  }
  return asLaneMetric(metric).scope === 'lane_merged'
}

export function metricIsLaneMergedId(
  analysis: MetricFigureSource,
  metricId: string | null | undefined,
): boolean {
  if (!metricId) {
    return false
  }
  return metricIsLaneMerged(analysis.metricResults?.[metricId])
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function columnTokens(column: unknown): { name: string; title: string } {
  if (!column || typeof column !== 'object') {
    return { name: '', title: '' }
  }
  const record = column as Record<string, unknown>
  return {
    name: String(record.name ?? '').trim().toLowerCase(),
    title: String(record.title ?? '').trim().toLowerCase(),
  }
}

function columnMatchesMetric(column: unknown, metric: MetricResult): boolean {
  const { name, title } = columnTokens(column)
  const label = metric.display.label.trim().toLowerCase()
  const metricId = metric.metricId.toLowerCase()
  const stableKey = metric.stableKey.toLowerCase()
  if (name) {
    if (name === label) {
      return true
    }
    if (metricId === name || metricId.endsWith(`:${name}`) || metricId.endsWith(`_${name}`)) {
      return true
    }
    if (stableKey === name || stableKey.endsWith(`:${name}`) || stableKey.endsWith(`_${name}`)) {
      return true
    }
  }
  if (title && title === label) {
    return true
  }
  return false
}

/**
 * The SurveyJS element (or matrix column) that carries this metric's scale
 * wording. A multi-column grid is only resolved when exactly one column
 * matches — never silently measure 0.
 */
function resolveSurveyColumn(survey: unknown, metric: MetricResult): unknown | null {
  const question = findSurveyQuestion<unknown>(survey, metric.questionId)
  if (!question || typeof question !== 'object') {
    return null
  }
  const columns = (question as { columns?: unknown }).columns
  if (!Array.isArray(columns) || columns.length === 0) {
    return question
  }
  if (columns.length === 1) {
    return columns[0]
  }
  const matched = columns.filter((column) => columnMatchesMetric(column, metric))
  if (matched.length === 1) {
    return matched[0]
  }
  return null
}

function resolveScaleAnchorWording(
  survey: unknown,
  metric: MetricResult,
  unit: string | undefined,
): MetricFigureScaleAnchors | undefined {
  if (!survey || !unit || !isRatingUnitId(unit)) {
    return undefined
  }
  const column = resolveSurveyColumn(survey, metric)
  if (!column) {
    return undefined
  }
  const [min, max] = ratingBounds(unit)
  const anchors = resolveScaleAnchors(column, min, max)
  if (!anchors.hasWording) {
    return undefined
  }
  return {
    minLabel: anchors.minLabel,
    midLabel: anchors.midLabel,
    maxLabel: anchors.maxLabel,
  }
}

/**
 * A grid metric's `baseSize` counts CELLS. Per-row respondent counts live in
 * `__row_base:<row>`. A row figure uses that key; a pooled grid figure uses
 * the minimum row base so the page never says n=200 for 50 respondents.
 */
function applyGridRowBase(
  figure: MetricFigure,
  metric: MetricResult,
  rowKey?: string,
): void {
  const breakdown = metric.breakdown
  if (!breakdown) {
    return
  }
  if (rowKey) {
    const exact = breakdown[`__row_base:${rowKey}`]
    if (isFiniteNumber(exact) && exact > 0) {
      figure.baseSize = exact
      return
    }
  }
  const rowBases: Array<number> = []
  for (const [key, value] of Object.entries(breakdown)) {
    if (!key.startsWith('__row_base:')) {
      continue
    }
    const suffix = key.slice('__row_base:'.length)
    if (suffix.includes('::')) {
      continue
    }
    if (isFiniteNumber(value) && value > 0) {
      rowBases.push(value)
    }
  }
  if (rowBases.length > 0) {
    figure.baseSize = Math.min(...rowBases)
  }
}

function applyRatingFrame(figure: MetricFigure, stats: RatingStatistics | null | undefined): void {
  if (!stats) {
    return
  }
  figure.unit = stats.unit
  // A figure's direction: none on a middle-is-best scale, whose mean has no better end.
  figure.higherIsBetter = stats.polarity === 'middle_is_best' ? undefined : stats.polarity === 'higher_is_better'
  figure.baseSize = stats.baseSize
  figure.favorablePercent = stats.favorablePercent
  figure.favorableLabel = stats.labels.favorable
}

/**
 * The breakdown key that holds this asset's figure.
 *
 * A grid metric keys its cells `{rowId}::{assetId}`, not by asset alone, and
 * analysis already accepts that spelling when it decides a scorecard has
 * per-asset columns. Reading only the bare `assetId` here made the page
 * disagree with the payload it was handed: every row resolved to nothing and
 * a two-asset comparison collapsed to one unlabelled column.
 *
 * Only an UNAMBIGUOUS suffix match counts, the same rule analysis applies: two
 * `::{assetId}` keys on one metric is a whole grid dumped onto the measure,
 * and picking one of them would show a row the reader cannot identify.
 */
function breakdownKeyForAsset(
  breakdown: Record<string, number> | null | undefined,
  assetId: string,
): string | null {
  if (!breakdown) {
    return null
  }
  if (assetId in breakdown) {
    return assetId
  }
  const suffix = `::${assetId}`
  const matching = Object.keys(breakdown).filter((key) => key.endsWith(suffix))
  return matching.length === 1 ? matching[0] : null
}

/**
 * Resolve one stamped `metricId` to a stated figure.
 *
 * Cache scope: not an LLM prompt. Pure lookup from `metricResults` plus
 * optional survey-column wording; no model call and no aggregation.
 */
export function resolveMetricFigure(
  analysis: MetricFigureSource,
  metricId: string,
  assetId?: string,
): MetricFigure | null {
  if (!metricId) {
    return null
  }
  const metric = analysis.metricResults?.[metricId]
  // No `display` means no label and no unit: a figure without either is a
  // number the reader cannot place, so there is nothing to state.
  if (!metric?.display) {
    return null
  }

  const figure: MetricFigure = {
    metricId: metric.metricId,
    label: (metric.display.shortLabel || metric.display.label).trim() || metric.display.label,
    nativeValue: isFiniteNumber(metric.nativeValue) ? metric.nativeValue : null,
    unit: metric.display.nativeUnitLabel,
    baseSize: metric.baseSize,
    questionText: metric.questionText ?? null,
  }

  applyRatingFrame(figure, metric.ratingStatistics)
  applyGridRowBase(figure, metric)

  if (assetId) {
    const sub = asLaneMetric(metric).perAsset?.[assetId]
    if (sub) {
      figure.assetId = assetId
      figure.nativeValue = isFiniteNumber(sub.nativeValue) ? sub.nativeValue : null
      figure.baseSize = sub.baseSize
      applyRatingFrame(figure, sub.ratingStatistics)
      applyGridRowBase(figure, sub)
      const scaleAnchors = resolveScaleAnchorWording(analysis.survey, metric, figure.unit)
      if (scaleAnchors) {
        figure.scaleAnchors = scaleAnchors
      }
      return figure
    }
    const assetKey = breakdownKeyForAsset(metric.breakdown, assetId)
    const nativeByAsset = assetKey == null ? undefined : metric.breakdown?.[assetKey]
    if (!isFiniteNumber(nativeByAsset)) {
      return null
    }
    figure.assetId = assetId
    figure.nativeValue = nativeByAsset
    applyRatingFrame(
      figure,
      metric.cellRatingStatistics?.[assetId] ??
        (assetKey == null ? undefined : metric.cellRatingStatistics?.[assetKey]),
    )
    applyGridRowBase(figure, metric, assetKey ?? assetId)
  } else if (asLaneMetric(metric).scope === 'lane_merged') {
    // 7.1: a lane-merged result has no pooled figure. Returning a blank
    // nativeValue would render as an empty KPI tile.
    return null
  }

  const scaleAnchors = resolveScaleAnchorWording(analysis.survey, metric, figure.unit)
  if (scaleAnchors) {
    figure.scaleAnchors = scaleAnchors
  }

  return figure
}
